/**
 * AdminCodesManager — /admin/codes
 * Admin panel: generate a batch of HomeLyph or D-Freeze codes, view all codes
 * with filtering by product/status, mark exported (copy to clipboard).
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import ToolSelector from '../../components/ToolSelector';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Ticket, Copy, Download, Loader2, Filter, Plus, KeyRound, XCircle } from 'lucide-react';

const AdminCodesManager = () => {
  const { API } = useContext(AuthContext);
  const [product, setProduct] = useState('homelyph');
  const [count, setCount] = useState(10);
  const [note, setNote] = useState('');
  const [generating, setGenerating] = useState(false);
  const [rows, setRows] = useState([]);
  const [productFilter, setProductFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  // Content-scoped access codes (course / event / study session unlocks).
  const [cTargetType, setCTargetType] = useState('event');
  const [cTargetId, setCTargetId] = useState('');
  const [cTargetLabel, setCTargetLabel] = useState('');
  const [cCount, setCCount] = useState(1);
  const [cGrantDays, setCGrantDays] = useState(30);
  const [cMaxRedemptions, setCMaxRedemptions] = useState(1);
  const [cNote, setCNote] = useState('');
  const [cGenerating, setCGenerating] = useState(false);
  const [cRows, setCRows] = useState([]);
  const [cLoading, setCLoading] = useState(false);
  const [lastCodes, setLastCodes] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (productFilter) params.set('product', productFilter);
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', '500');
      const { data } = await axios.get(`${API}/access-codes?${params.toString()}`);
      setRows(data.codes || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [API, productFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // --- Content codes -----------------------------------------------------
  const loadContent = useCallback(async () => {
    setCLoading(true);
    try {
      const { data } = await axios.get(`${API}/content-codes?limit=200`);
      setCRows(data.codes || []);
    } catch { setCRows([]); }
    finally { setCLoading(false); }
  }, [API]);

  useEffect(() => { loadContent(); }, [loadContent]);

  const generateContent = async () => {
    if (!cTargetId.trim() || !cTargetLabel.trim()) {
      toast.error('Target ID and label are required');
      return;
    }
    setCGenerating(true);
    try {
      const { data } = await axios.post(`${API}/content-codes/generate`, {
        target_type:     cTargetType,
        target_id:       cTargetId.trim(),
        target_label:    cTargetLabel.trim(),
        count:           Number(cCount) || 1,
        grant_days:      cGrantDays ? Number(cGrantDays) : null,
        max_redemptions: Number(cMaxRedemptions) || 1,
        note:            cNote.trim() || null,
      });
      setLastCodes(data.codes || []);
      toast.success(`${data.count} content code(s) generated`);
      setCNote('');
      loadContent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate content codes');
    } finally { setCGenerating(false); }
  };

  const revokeContent = async (code) => {
    if (!window.confirm(`Revoke code ${code}? (Only works if not yet redeemed.)`)) return;
    try {
      await axios.delete(`${API}/content-codes/${encodeURIComponent(code)}`);
      toast.success('Code revoked');
      loadContent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke');
    }
  };

  const copyLast = () => {
    if (!lastCodes.length) return;
    const text = lastCodes.map((c) => c.code).join('\n');
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${lastCodes.length} code(s)`);
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/access-codes/generate`, {
        product, count: Number(count) || 1, note: note.trim() || null,
      });
      toast.success(`${data.count} codes generated`);
      setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate');
    } finally { setGenerating(false); }
  };

  // --- Tier batch codes (flexible tier + duration) ---------------------
  const [tTier, setTTier]         = useState('pro');
  const [tDays, setTDays]         = useState(90);
  const [tCount, setTCount]       = useState(10);
  const [tMaxUses, setTMaxUses]   = useState(1);
  const [tLabel, setTLabel]       = useState('');
  const [tNote, setTNote]         = useState('');
  const [tGenerating, setTGenerating] = useState(false);
  const [tLastCodes, setTLastCodes]   = useState([]);

  const generateTier = async () => {
    setTGenerating(true);
    try {
      const { data } = await axios.post(`${API}/access-codes/generate-tier`, {
        tier:          tTier,
        duration_days: parseInt(tDays, 10) || 30,
        count:         parseInt(tCount, 10) || 1,
        max_uses:      Math.max(1, parseInt(tMaxUses, 10) || 1),
        label:         tLabel.trim() || undefined,
        note:          tNote.trim() || undefined,
      });
      setTLastCodes(data.codes || []);
      toast.success(`${data.count} × ${tTier.toUpperCase()} · ${data.duration_days}d codes generated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate tier codes');
    } finally {
      setTGenerating(false);
    }
  };

  const copyOne = async (code) => {
    try {
      await navigator.clipboard?.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const revokeAccessCode = async (code) => {
    if (!window.confirm(`Revoke code ${code}? (Only works if not yet redeemed.)`)) return;
    try {
      await axios.delete(`${API}/access-codes/${encodeURIComponent(code)}`);
      toast.success('Code revoked');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not revoke');
    }
  };

  const copyTierLast = async () => {
    if (!tLastCodes.length) return;
    const text = tLastCodes.map((c) => c.code).join('\n');
    await navigator.clipboard?.writeText(text);
    toast.success(`Copied ${tLastCodes.length} code(s)`);
  };

  const downloadTierCSV = () => {
    if (!tLastCodes.length) return;
    const csv = 'code,tier,duration_days,label\n' +
      tLastCodes.map((c) => `${c.code},${tTier},${tDays},"${(tLabel || '').replace(/"/g, '""')}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tier-codes-${tTier}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyAll = () => {
    const text = rows.filter((r) => r.status === 'unused').map((r) => r.code).join('\n');
    navigator.clipboard?.writeText(text);
    toast.success(`Copied ${rows.filter((r) => r.status === 'unused').length} unused codes`);
  };

  const downloadCSV = () => {
    const header = 'code,product,tier,status,created_at,redeemed_at,expires_at,note\n';
    const csv = header + rows.map((r) => [
      r.code, r.product, r.tier, r.status, r.created_at || '', r.redeemed_at || '', r.expires_at || '', (r.note || '').replace(/,/g, ';'),
    ].join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `access-codes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-codes-page">
      <ToolSelector />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Ticket className="w-3 h-3" /> Admin · Access Codes
          </div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Access Codes</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Two flavors: <strong>tier codes</strong> (HomeLyph / D-Freeze — 90-day Pro or
            Business upgrades shipped with hardware) and <strong>content codes</strong>
            (password-like unlocks for a specific event, study session, or gated course).
          </p>
        </div>

        {/* Generate form */}
        <div className="pro-card p-5" data-testid="admin-codes-generate">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <Plus className="w-3 h-3" /> Generate a batch
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Product</label>
              <select
                value={product}
                onChange={(e) => setProduct(e.target.value)}
                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="admin-codes-product"
              >
                <option value="homelyph">HomeLyph · Pro (90d)</option>
                <option value="dfreeze">D-Freeze · Business (90d)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Count</label>
              <Input type="number" min={1} max={1000} value={count} onChange={(e) => setCount(e.target.value)} data-testid="admin-codes-count" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Batch note (optional)</label>
              <Textarea rows={1} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g., Feb 2026 hardware shipment" data-testid="admin-codes-note" />
            </div>
          </div>
          <div className="mt-3">
            <Button onClick={generate} disabled={generating} className="rounded-full font-bold" data-testid="admin-codes-generate-btn">
              {generating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</> : <>Generate {count} codes <Ticket className="ml-1.5 w-3.5 h-3.5" /></>}
            </Button>
          </div>
        </div>

        {/* Flexible tier code batch — pick ANY tier + ANY duration */}
        <div className="pro-card p-5 mt-6" data-testid="admin-tier-codes-generate">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Tier code batch (custom tier + duration)
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Mint N codes that each grant a subscription tier for a chosen number of days.
            Perfect for partner promos, giveaways, or team seat bundles.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Tier</label>
              <select
                value={tTier}
                onChange={(e) => setTTier(e.target.value)}
                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="tier-codes-tier"
              >
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Duration (days)</label>
              <Input type="number" min={1} max={3650} value={tDays} onChange={(e) => setTDays(e.target.value)} data-testid="tier-codes-days" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Count</label>
              <Input type="number" min={1} max={1000} value={tCount} onChange={(e) => setTCount(e.target.value)} data-testid="tier-codes-count" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Max uses / code</label>
              <Input type="number" min={1} max={100000} value={tMaxUses} onChange={(e) => setTMaxUses(e.target.value)} data-testid="tier-codes-max-uses" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Label (admin-facing)</label>
              <Input value={tLabel} onChange={(e) => setTLabel(e.target.value)} placeholder={`${tTier.charAt(0).toUpperCase() + tTier.slice(1)} · ${tDays} days`} data-testid="tier-codes-label" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Note (optional, internal)</label>
            <Textarea rows={1} value={tNote} onChange={(e) => setTNote(e.target.value)} placeholder="e.g., Q2 partner giveaway — Acme Co." data-testid="tier-codes-note" />
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button onClick={generateTier} disabled={tGenerating} className="rounded-full font-bold" data-testid="tier-codes-generate-btn">
              {tGenerating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</> : <>Generate {tCount} tier codes <Ticket className="ml-1.5 w-3.5 h-3.5" /></>}
            </Button>
            {tLastCodes.length > 0 && (
              <>
                <Button size="sm" variant="outline" onClick={copyTierLast} className="rounded-full" data-testid="tier-codes-copy-last">
                  <Copy className="w-3 h-3 mr-1" /> Copy {tLastCodes.length}
                </Button>
                <Button size="sm" variant="outline" onClick={downloadTierCSV} className="rounded-full" data-testid="tier-codes-csv">
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
              </>
            )}
          </div>
          {tLastCodes.length > 0 && (
            <div className="mt-3 rounded-md border border-primary/30 bg-primary/5 p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">Just generated · click any code to copy</div>
              <div className="text-xs font-mono max-h-64 overflow-auto space-y-0.5" data-testid="tier-codes-last-list">
                {tLastCodes.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => copyOne(c.code)}
                    className="w-full text-left px-2 py-1 rounded hover:bg-primary/10 flex items-center justify-between group"
                    data-testid={`tier-code-copy-${c.code}`}
                  >
                    <span>{c.code}</span>
                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-70" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filters + list */}
        <div className="mt-6 flex items-end justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Product</label>
              <select value={productFilter} onChange={(e) => setProductFilter(e.target.value)} className="ml-1 rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="admin-codes-filter-product">
                <option value="">All</option>
                <option value="homelyph">HomeLyph</option>
                <option value="dfreeze">D-Freeze</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Status</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="ml-1 rounded-md border border-input bg-background px-2 py-1 text-xs" data-testid="admin-codes-filter-status">
                <option value="">All</option>
                <option value="unused">Unused</option>
                <option value="used">Used</option>
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={load} className="rounded-full"><Filter className="w-3 h-3 mr-1" /> Refresh</Button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={copyAll} className="rounded-full" data-testid="admin-codes-copy"><Copy className="w-3 h-3 mr-1" /> Copy unused</Button>
            <Button size="sm" variant="outline" onClick={downloadCSV} className="rounded-full" data-testid="admin-codes-csv"><Download className="w-3 h-3 mr-1" /> CSV</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm mt-4"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
        ) : (
          <div className="pro-card mt-3 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-widest font-bold">
                <tr>
                  <th className="text-left px-4 py-2">Code</th>
                  <th className="text-left px-4 py-2">Product</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Uses</th>
                  <th className="text-left px-4 py-2">Redeemed by</th>
                  <th className="text-left px-4 py-2">Expires</th>
                  <th className="text-left px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="7" className="text-center text-muted-foreground italic py-6">No codes yet</td></tr>
                ) : rows.map((r) => {
                  const used = (r.use_count || 0);
                  const max  = (r.max_uses || 1);
                  const canRevoke = used === 0 && r.status !== 'used';
                  return (
                  <tr key={r.id} className="border-t border-border" data-testid={`admin-codes-row-${r.code}`}>
                    <td className="px-4 py-2 font-mono text-xs">
                      <button
                        type="button"
                        onClick={() => copyOne(r.code)}
                        className="hover:underline inline-flex items-center gap-1 group"
                        data-testid={`admin-codes-copy-${r.code}`}
                      >
                        {r.code}
                        <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60" />
                      </button>
                    </td>
                    <td className="px-4 py-2 capitalize">{r.product}</td>
                    <td className="px-4 py-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                        r.status === 'used' ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-2 text-xs">{used} / {max}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[240px]">{r.redeemed_by || '—'}</td>
                    <td className="px-4 py-2 text-xs">{r.expires_at?.slice(0, 10) || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      {canRevoke && (
                        <button
                          type="button"
                          onClick={() => revokeAccessCode(r.code)}
                          className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`admin-codes-revoke-${r.code}`}
                        >
                          <XCircle className="w-3 h-3" /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ================= Content-scoped access codes ================= */}
        <div className="mt-10 mb-4">
          <div className="eyebrow text-primary inline-flex items-center gap-1.5">
            <KeyRound className="w-3 h-3" /> Admin · Content Codes
          </div>
          <h2 className="font-heading text-2xl font-extrabold mt-1">Event / Study / Course codes</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Mint a code that unlocks a <em>specific</em> event, study session, or gated course
            without changing the member&rsquo;s tier. Set <strong>max redemptions</strong> to
            1 for personal invites, or higher for a shared event password.
          </p>
        </div>

        <div className="pro-card p-5" data-testid="admin-content-codes-generate">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Target type</label>
              <select
                value={cTargetType}
                onChange={(e) => setCTargetType(e.target.value)}
                className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="content-code-target-type"
              >
                <option value="course">Course</option>
                <option value="event">Event</option>
                <option value="session">Study session</option>
                <option value="lesson">Lesson</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Target ID</label>
              <Input
                value={cTargetId}
                onChange={(e) => setCTargetId(e.target.value)}
                placeholder="e.g. course-abc123"
                data-testid="content-code-target-id"
              />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Label (shown to members)</label>
              <Input
                value={cTargetLabel}
                onChange={(e) => setCTargetLabel(e.target.value)}
                placeholder="e.g. Feb 20 Pro Workshop"
                data-testid="content-code-target-label"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">How many codes</label>
              <Input type="number" min={1} max={500} value={cCount} onChange={(e) => setCCount(e.target.value)} data-testid="content-code-count" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Grant days (blank = lifetime)</label>
              <Input type="number" min={1} max={365} value={cGrantDays} onChange={(e) => setCGrantDays(e.target.value)} data-testid="content-code-grant-days" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Max redemptions / code</label>
              <Input type="number" min={1} max={10000} value={cMaxRedemptions} onChange={(e) => setCMaxRedemptions(e.target.value)} data-testid="content-code-max-redemptions" />
            </div>
            <div className="md:col-span-3">
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Batch note (optional)</label>
              <Textarea rows={1} value={cNote} onChange={(e) => setCNote(e.target.value)} placeholder="e.g. VIP list for Feb workshop" data-testid="content-code-note" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              onClick={generateContent}
              disabled={cGenerating}
              className="rounded-full font-bold"
              data-testid="content-code-generate-btn"
            >
              {cGenerating ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</> : <>Mint {cCount || 1} code(s) <KeyRound className="ml-1.5 w-3.5 h-3.5" /></>}
            </Button>
            {lastCodes.length > 0 && (
              <Button size="sm" variant="outline" onClick={copyLast} className="rounded-full" data-testid="content-code-copy-last">
                <Copy className="w-3 h-3 mr-1" /> Copy last batch
              </Button>
            )}
          </div>

          {lastCodes.length > 0 && (
            <div className="mt-4 rounded-lg border border-primary/30 bg-primary/5 p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">Just generated</div>
              <div className="font-mono text-sm space-y-0.5">
                {lastCodes.map((c) => <div key={c.id}>{c.code}</div>)}
              </div>
            </div>
          )}
        </div>

        {/* Existing content codes */}
        <div className="mt-6">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">All content codes</div>
            <Button size="sm" variant="outline" onClick={loadContent} className="rounded-full">
              <Filter className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>

          {cLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm mt-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="pro-card mt-2 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[10px] uppercase tracking-widest font-bold">
                  <tr>
                    <th className="text-left px-4 py-2">Code</th>
                    <th className="text-left px-4 py-2">Target</th>
                    <th className="text-left px-4 py-2">Redemptions</th>
                    <th className="text-left px-4 py-2">Grant days</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {cRows.length === 0 ? (
                    <tr><td colSpan="6" className="text-center text-muted-foreground italic py-6">No content codes yet</td></tr>
                  ) : cRows.map((r) => (
                    <tr key={r.id} className="border-t border-border" data-testid={`content-code-row-${r.code}`}>
                      <td className="px-4 py-2 font-mono text-xs">{r.code}</td>
                      <td className="px-4 py-2 text-xs">
                        <div className="font-semibold text-foreground">{r.target_label}</div>
                        <div className="text-muted-foreground">{r.target_type} · {r.target_id}</div>
                      </td>
                      <td className="px-4 py-2 text-xs">{r.redemption_count || 0} / {r.max_redemptions || 1}</td>
                      <td className="px-4 py-2 text-xs">{r.grant_days ? `${r.grant_days}d` : 'Lifetime'}</td>
                      <td className="px-4 py-2">
                        <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                          r.status === 'revoked' ? 'bg-rose-500/15 text-rose-600'
                          : r.status === 'used'  ? 'bg-muted text-muted-foreground'
                          : r.status === 'shared'? 'bg-amber-500/15 text-amber-700'
                          : 'bg-primary/10 text-primary'
                        }`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        {(r.redemption_count || 0) === 0 && r.status !== 'revoked' && (
                          <button
                            type="button"
                            onClick={() => revokeContent(r.code)}
                            className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1"
                            data-testid={`content-code-revoke-${r.code}`}
                          >
                            <XCircle className="w-3 h-3" /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// (Content-codes panel is appended below by injection.)
export default AdminCodesManager;
